import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { User as UserType } from '@supabase/supabase-js';
import { Settings, LogOut, User, Users, Heart } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';
import type { Profile, Band } from '../types/database';

interface UserMenuProps {
  user: UserType;
  profile: Profile | null;
  onSignOut: () => void;
}

type BandWithRole = Band & { role: string };

export function UserMenu({ user, profile, onSignOut }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [userBands, setUserBands] = useState<BandWithRole[]>([]);
  const [loadingBands, setLoadingBands] = useState(false);
  const [currentProfile, setCurrentProfile] = useState<Profile | null>(profile);
  const menuRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Subscribe to profile changes in real-time
  useEffect(() => {
    if (!user) {
      setCurrentProfile(null);
      return;
    }

    let mounted = true;

    // Set initial profile
    setCurrentProfile(profile);

    // Set up real-time subscription to profile changes
    const channel = supabase
      .channel(`user-profile-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          console.log('UserMenu: Profile change detected:', payload);
          
          if (!mounted) return;

          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            // Fetch the complete profile data to ensure we have all fields
            try {
              const { data: updatedProfile, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('user_id', user.id)
                .single();

              if (error) {
                console.error('Error fetching updated profile:', error);
                return;
              }

              console.log('UserMenu: Setting updated profile:', updatedProfile);
              if (mounted) {
                setCurrentProfile(updatedProfile);
              }
            } catch (error) {
              console.error('Error in profile subscription:', error);
            }
          } else if (payload.eventType === 'DELETE') {
            if (mounted) {
              setCurrentProfile(null);
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('UserMenu: Profile subscription status:', status);
      });

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [user, profile]);

  // Update profile when prop changes (for initial load)
  useEffect(() => {
    if (profile && (!currentProfile || profile.id !== currentProfile.id)) {
      console.log('UserMenu: Updating profile from props:', profile);
      setCurrentProfile(profile);
    }
  }, [profile, currentProfile]);

  useEffect(() => {
    async function fetchUserBands() {
      if (!currentProfile) return;

      try {
        setLoadingBands(true);
        const { data: memberships, error } = await supabase
          .from('band_members')
          .select(`
            role,
            band:bands (
              id,
              name,
              avatar_url,
              slug
            )
          `)
          .eq('profile_id', currentProfile.id)
          .order('joined_at', { ascending: false });

        if (error) throw error;

        const bands = memberships
          ?.map(m => ({
            ...m.band,
            role: m.role,
          }))
          .filter((band): band is BandWithRole => band !== null);

        setUserBands(bands || []);
      } catch (err) {
        console.error('Error fetching user bands:', err);
      } finally {
        setLoadingBands(false);
      }
    }

    if (isOpen && currentProfile) {
      fetchUserBands();
    }
  }, [currentProfile, isOpen]);

  // Reset bands when profile changes to ensure fresh data
  useEffect(() => {
    setUserBands([]);
  }, [currentProfile?.id]);

  const handleMenuItemClick = () => {
    setIsOpen(false);
  };

  // Generate a unique key for the avatar to force re-render
  const avatarKey = currentProfile?.avatar_url 
    ? `${currentProfile.avatar_url}-${currentProfile.updated_at || Date.now()}`
    : 'no-avatar';

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 focus:outline-none"
      >
        <div className="w-10 h-10 rounded-full bg-gray-custom/10 flex items-center justify-center overflow-hidden">
          {currentProfile?.avatar_url ? (
            <img
              src={`${currentProfile.avatar_url}?t=${Date.now()}`}
              alt={currentProfile.artist_name || ''}
              className="w-full h-full object-cover"
              key={avatarKey}
              loading="lazy"
            />
          ) : (
            <User className="w-6 h-6 text-light/60" />
          )}
        </div>
        {currentProfile && (
          <span className="hidden md:block text-light/80 hover:text-light transition-colors hidden md:flex">
            {currentProfile.artist_name || currentProfile.full_name}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 rounded-lg bg-white shadow-lg py-1">
          {loadingBands ? (
            <div className="text-center py-3 text-gray-500 text-sm">
              Loading bands...
            </div>
          ) : userBands.length > 0 && (
            <>
              <div className="text-xs text-gray-400 w-full text-center mt-2">{t('nav.yourBands')}</div>
              {userBands.map((band) => (
                <Link
                  key={band.id}
                  to={`/bands/${band.slug}`}
                  onClick={handleMenuItemClick}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                >
                  <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {band.avatar_url ? (
                      <img
                        src={band.avatar_url}
                        alt={band.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <Users className="w-3 h-3 text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 truncate">
                    <span className="block truncate">{band.name}</span>
                    <span className="text-xs text-gray-500">{band.role}</span>
                  </div>
                </Link>
              ))}
              <div className="border-t border-gray-100 my-1"></div>
            </>
          )}

          <div className="text-xs text-gray-400 w-full text-center mt-2">MENU</div>
          {currentProfile ? (
            <Link
              to="/profile"
              onClick={handleMenuItemClick}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
            >
              <User className="w-4 h-4" />
              <span>{t('nav.profile')}</span>
            </Link>
          ) : (
            <Link
              to="/create-profile"
              onClick={handleMenuItemClick}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
            >
              <User className="w-4 h-4" />
              <span>{t('nav.createProfile')}</span>
            </Link>
          )}
          
          <Link
            to="/settings"
            onClick={handleMenuItemClick}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
          >
            <Settings className="w-4 h-4" />
            <span>{t('nav.settings')}</span>
          </Link>
          <button
            onClick={() => {
              handleMenuItemClick();
              onSignOut();
            }}
            className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
          >
            <LogOut className="w-4 h-4" />
            <span>{t('nav.signOut')}</span>
          </button>
        </div>
      )}
    </div>
  );
}